import { ComponentCoordinate, HgsArticle } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, computed, input, signal } from '@angular/core';
import { HgsSvgFuseboxComponent } from './components/hgs-svg-fusebox.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';
import { Button } from 'primeng/button';

// TODO: will be replaced with ticket
// https://linear.app/ahead-automotive/issue/QI-1523/be-follow-up-hgs-api
const DUMMY_SYSTEM = {
  systemId: '',
  systemName: {
    language: 'de',
    title: '',
  },
  dateTo: '',
  imagePlacementUrl: '',
  components: [],
};

export type SystemComponent = {
  id: string;
  componentId: string;
  genericArticleNos?: number[];
  genericArticleNumbers?: number[];
  group?: { language: string; title: string };
  name?: { language: string; title: string };
  text?: { language: string; title: string };
  placementText?: { language: string; title: string };
  shortName?: string;
  showInComponentList: boolean;
  type?: string;
  xAbs: string;
  yAbs: string;
};

export type SystemImage = {
  components?: SystemComponent[];
  imagePlacementUrl?: string;
  systemId: string;
  systemName?: { language: string; title: string };
};

@Component({
  selector: 'app-hgs-fusebox',
  template: `
    @if (data() && data()?.text) {
      <div class="flex h-[88vh] flex-col gap-6">
        <div>
          <h2 class="mb-3 w-full overflow-hidden text-ellipsis whitespace-nowrap text-xl font-bold lg:text-2xl">
            {{ image()['systemName']['title'] }}
          </h2>
          <app-hgs-svg-fusebox [system]="image()" [shownBox]="componentToShow()" class="block w-full" imgClass="rounded-lg shadow-md" />
        </div>

        <div class="relative flex-1 overflow-hidden rounded-lg border border-gray-300 shadow-md">
          <div class="h-full overflow-y-auto">
            <table class="w-full border-separate border-spacing-0">
              <thead class="sticky top-0 z-10 bg-gray-100 shadow-sm">
                <tr>
                  <th class="border-b border-gray-300 px-3 py-3 text-left text-sm font-bold text-gray-800 lg:px-6 lg:py-4 lg:text-lg">
                    {{ 'documents.hgs.component' | transloco }}
                  </th>
                  <th class="border-b border-gray-300 px-8 py-3 text-right text-sm font-bold text-gray-800 lg:py-4 lg:text-lg">
                    {{ 'documents.hgs.location' | transloco }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                @for (comp of components(); track uniqueId(comp) + $index) {
                  <tr [attr.class]="uniqueId(comp) === uniqueId(componentToShow()) ? 'bg-blue-100' : 'bg-white hover:bg-gray-50 transition-colors'">
                    <td class="border-b border-gray-100 px-3 py-3 text-sm lg:px-6 lg:py-5 lg:text-lg">
                      <span class="font-semibold text-blue-900">{{ comp['component']?.['shortName'] }}</span>
                      <span class="text-gray-700">- {{ comp['component']?.['text']?.['title'] }}</span>
                    </td>
                    <td class="border-b border-gray-100 px-3 py-2 text-right lg:text-lg">
                      @if (hasSufficientCoordinates(comp)) {
                        <p-button severity="info" class="items-left shadow-sm transition-all sm:whitespace-nowrap" (click)="onClick(comp)">
                          {{ 'documents.hgs.showPosition' | transloco }}
                        </p-button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }
  `,
  imports: [CommonModule, TranslocoPipe, MarkdownModule, HgsSvgFuseboxComponent, Button],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class HgsFuseboxComponent {
  data = input<HgsArticle | undefined>();
  image = computed(() => ({
    ...DUMMY_SYSTEM,
    imagePlacementUrl: this.data()?.data?.fuseBoxDocument?.['imageUrl'] ?? undefined,
    systemName: { language: 'de', title: this.data()?.data?.fuseBoxDocument?.['title'] ?? '' },
  }));
  components = computed(() => this.data()?.data?.fuseBoxDocument?.['componentCoordinates'] || []);

  componentToShow = signal<ComponentCoordinate | undefined>(undefined);

  uniqueId(component: ComponentCoordinate | undefined): string {
    return component ? `${component.component.id}-${component['x1']}-${component['x2']}--${component['y1']}-${component['y2']}` : '';
  }

  onClick(component: ComponentCoordinate | undefined) {
    this.componentToShow.set(component);
  }

  hasSufficientCoordinates(component: ComponentCoordinate): boolean {
    return component.x1 !== undefined && component.x2 !== undefined && component.y1 !== undefined && component.y2 !== undefined;
  }
}
